// BUILD-046 Owner direction of 2026-10-21 (cycle A, correction A): the pure part of the SILENT re-read of a loaded list - what a list becomes when a background read answers, and
// what is said when the background read fails.
//
// Why a second path beside `load`: `load` (listStateModel.ts) enters the `loading` phase, which clears the rows and shows "Loading ..." - right for the first read and for
// Retry of a failed list, wrong for a list the person is looking at when the Phoenix day turns. A silent re-read keeps the rows on screen until the new ones arrive, then
// swaps them (the rows are keyed by task id, so an open form, a typed note, the scroll position and the selection stay where they are). If the re-read fails the rows stay too,
// and the page says so truthfully (`describeRefreshNote`): never an empty list, never "no results", never a pretended success.
//
// Pure module: no React, no I/O. Type-only imports. Erasable TypeScript only.

import type { ListError, ListName, ListState } from './listStateModel';

/** A background re-read of one list failed. The list keeps its previous rows; `retrying` is true while the person's Retry is in flight. */
export interface RefreshNote {
  error: ListError;
  retrying: boolean;
}

export type RefreshNotes = Readonly<Partial<Record<ListName, RefreshNote>>>;

/** The answer to "the server answered, but not with a list". Kept the same words as the list state model's own invalid-answer error. */
export const REFRESH_INVALID_ANSWER: ListError = Object.freeze({
  kind: 'invalid-response',
  status: 200,
  code: null,
  message: 'The server answered, but not in the expected form.',
});

/** Only a list that holds an answer (data, or a genuine empty) can be refreshed silently; a loading list is already being read and a failed list has its own Retry. */
export function canRefresh<T>(state: ListState<T>): boolean {
  return state.phase === 'data' || state.phase === 'empty';
}

/**
 * The list after a successful silent re-read: the new items (`data`), or a genuine `empty` when the server answered with none. The request position (epoch, seq) is kept, so an
 * answer to an older `load` can still never be applied. A state that cannot be refreshed, or items that are not a list, leave the state as it is (same object).
 */
export function refreshedState<T>(state: ListState<T>, items: unknown): ListState<T> {
  if (!canRefresh(state) || !Array.isArray(items)) return state;
  const next = items as readonly T[];
  return { phase: next.length === 0 ? 'empty' : 'data', epoch: state.epoch, seq: state.seq, items: next.length === 0 ? [] : next, error: null };
}

export type RefreshNoteView = { kind: 'refreshing'; text: string } | { kind: 'failed'; headline: string; detail: string; canRetry: true };

/** How each list is named in the note. */
export const REFRESH_LIST_LABELS: Readonly<Record<ListName, string>> = Object.freeze({ myTasks: 'My tasks', shared: 'Shared tasks', oversight: 'Team Work', team: 'Team Work' });

/**
 * The list the selected tab is drawn from (the one whose refresh note belongs above it), or null when the tab draws from no list that a silent re-read covers:
 * Assigned by Me (its own store), and the Team / Company Completed filter (a paged list of completed tasks, which are never overdue; it is not re-read).
 */
export function listShownBy(input: { issuedOpen: boolean; view: string; teamKind: string | null; teamFilter: string }): ListName | null {
  if (input.issuedOpen) return null;
  if (input.view === 'my-work') return 'myTasks';
  if (input.view === 'group-shared') return 'shared';
  if (input.view === 'team-work') {
    if (input.teamKind === 'oversight') return input.teamFilter === 'completed' ? null : 'oversight';
    return 'team';
  }
  return null;
}

/** What the page says about a failed background re-read of `listLabel` ("My tasks"). `null` when there is nothing to say. */
export function describeRefreshNote(note: RefreshNote | undefined, listLabel: string): RefreshNoteView | null {
  if (note === undefined) return null;
  if (note.retrying) return { kind: 'refreshing', text: `Refreshing ${listLabel}\u2026` };
  const reason = typeof note.error.message === 'string' && note.error.message !== '' ? ` ${note.error.message}` : '';
  return {
    kind: 'failed',
    headline: `${listLabel} could not be refreshed.`,
    detail: `You are seeing the tasks as they were last loaded, so a task may have changed since and an Overdue mark may be out of date.${reason}`,
    canRetry: true,
  };
}
